import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { truncate } from '~/lib/Utils';
import GenericCheckboxes from './GenericCheckboxes';

export type CheckboxSelectorValue = {
    id: string;
    value: unknown;
    label: string;
};

interface CheckboxSelectorProps<ValueType = unknown> {
    allValues?: Array<CheckboxSelectorValue>; // all items
    selectedValues?: Array<ValueType>; // Array of selected id values
    id: string; // ID for the EventTypeCheckboxes component
    initialOpen?: boolean; // Whether the dropdown is initially open
    onChange: (selected: Array<ValueType>) => void; // Callback function for when the selection changes
    defaultAll?: boolean; // Whether to select all by default
    className?: string; // Additional class names for styling
    itemNamePlural?: string; // a name that describes the generic meaning of the items listed (i.e event types) in plural
    maxDisplay?: number;
    containerClassName?: string;
    renderLabel?: (value: CheckboxSelectorValue) => React.ReactNode;
}
export type CompactEventTypeImperativeHandle = {
    close: () => void; // Method to close the dropdown
};

const CheckboxSelector = <ValueType = unknown,>(
    {
        allValues = [],
        selectedValues,
        id,
        initialOpen,
        onChange,
        defaultAll,
        className,
        maxDisplay = 2,
        itemNamePlural = 'items',
        containerClassName,
        renderLabel,
    }: CheckboxSelectorProps<ValueType>,
    ref: React.Ref<CompactEventTypeImperativeHandle>,
) => {
    const [isOpen, setIsOpen] = useState(!!initialOpen);

    useImperativeHandle(ref, () => {
        return {
            close: () => setIsOpen(false),
        };
    });

    const allowedSelectedValues = useMemo(() => {
        if (allValues && allValues?.length) {
            return allValues.filter(
                (
                    t, // only allows values amongst allValues to be selected
                ) => selectedValues?.includes(t.value as ValueType),
            );
        }
        return [];
    }, [allValues, selectedValues]);

    const areAllSelected = // true if all allowed values are selected
        allowedSelectedValues?.length === allValues?.length ||
        (!selectedValues?.length && defaultAll);

    const collapsedVisibleSelectedValues: Array<CheckboxSelectorValue> =
        useMemo(() => {
            // values that will be visible when the selector is collapsed
            if (!areAllSelected && allowedSelectedValues?.length) {
                return allowedSelectedValues.slice(0, maxDisplay);
            }
            return [];
        }, [areAllSelected, allowedSelectedValues, maxDisplay]);

    const extraText = useMemo(() => {
        if (areAllSelected) {
            return `All ${itemNamePlural}`;
        } else {
            if ((allowedSelectedValues?.length ?? 0) > maxDisplay) {
                return `+${(allowedSelectedValues?.length ?? 0) - maxDisplay} more`;
            } else if (!allowedSelectedValues?.length) {
                return `Select ${itemNamePlural}...`;
            }
        }
        return '';
    }, [areAllSelected, itemNamePlural, allowedSelectedValues?.length, maxDisplay]);

    return (
        <div className={`rounded-lg ${className} `}>
            <div
                onClick={() => !isOpen && setIsOpen(true)}
                className={`${!isOpen ? 'items-center  p-1' : 'items-start px-2'} border border-transparent bg-white rounded-md flex justify-between cursor-pointer ${containerClassName}`}
            >
                <div className="flex-1">
                    {!isOpen && (
                        <div className="flex items-center">
                            {collapsedVisibleSelectedValues.map(
                                (value: CheckboxSelectorValue) => (
                                    <div
                                        key={value.id ?? value.value}
                                        className="px-3 py-1 mr-2 text-sm text-primary-700 border border-primary-100 bg-primary-50 rounded"
                                    >
                                        {truncate(value.label)}
                                    </div>
                                ),
                            )}
                            <div className="text-primary-700 text-sm py-1 px-3">
                                {extraText}
                            </div>
                        </div>
                    )}

                    {isOpen && (
                        <GenericCheckboxes<ValueType>
                            id={id}
                            options={allValues}
                            selectedValues={selectedValues}
                            onChange={onChange}
                            defaultAll={defaultAll}
                            renderLabel={renderLabel}
                        />
                    )}
                </div>
                <div
                    onClick={() => setIsOpen(!isOpen)}
                    className="text-gray-500 group-hover:text-gray-700 p-2"
                >
                    {isOpen && <ChevronUpIcon className="w-4 h-4" />}
                    {!isOpen && <ChevronDownIcon className="w-4 h-4" />}
                </div>
            </div>
        </div>
    );
};

export default forwardRef(CheckboxSelector);
